import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type CaseJobsResult, type DispatchBoardResult } from '@whinerp/client';
import { formatDateTime, todayIn } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/** Field service (G-201): the dispatcher's day on the queue, and a case's own visits. */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';
const time = (iso: string, locale: string) => new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

export function DispatchPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [day, setDay] = useState(todayIn(format));
  const board = useQuery<DispatchBoardResult, ApiError>({ queryKey: ['dispatch', day], queryFn: () => api.dispatchBoard({ query: { day } }) });
  const assign = useMutation({
    mutationFn: (input: { visitId: string; technicianId: string | null }) => api.assignJob({ body: input }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['dispatch', day] }),
  });
  if (!board.data) return null;
  return (
    <Panel data-region="dispatch">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{t('dispatch.title')}</SectionLabel>
          <input className={`${INPUT} ms-auto`} type="date" data-dispatch-day aria-label={t('dispatch.day')} value={day} onChange={(event) => setDay(event.target.value)} />
        </div>
        {board.data.jobs.length === 0 ? (
          <p className="text-meta text-ink-meta">{t('dispatch.none')}</p>
        ) : (
          <ul className="flex flex-col gap-1 text-dense">
            {board.data.jobs.map((job) => (
              <li key={job.visitId} data-job={job.visitId} data-job-assigned={job.technicianId !== null} className="flex flex-wrap items-center gap-2">
                <span className="text-ink-meta">{time(job.startsAt, format.locale)}-{time(job.endsAt, format.locale)}</span>
                <a className="text-ink underline" href={`/cases/${job.caseId}`}>{job.caseNumber}</a>
                <span className="text-ink-600">{job.subject}</span>
                {job.location ? <span className="text-ink-meta">{job.location}</span> : null}
                <select className={`${INPUT} ms-auto`} data-job-technician aria-label={t('dispatch.technician')} value={job.technicianId ?? ''}
                  disabled={job.status !== 'planned'} onChange={(event) => assign.mutate({ visitId: job.visitId, technicianId: event.target.value || null })}>
                  <option value="">{t('dispatch.unassigned')}</option>
                  {board.data.technicians.map((person) => <option key={person.userId} value={person.userId}>{person.name}</option>)}
                </select>
              </li>
            ))}
          </ul>
        )}
        {assign.error ? <p className="text-dense text-danger-text" data-dispatch-error>{errorText(assign.error as ApiError)}</p> : null}
      </div>
    </Panel>
  );
}

export function CaseJobsPanel({ caseId }: { caseId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const key = ['case-jobs', caseId];
  const jobs = useQuery<CaseJobsResult, ApiError>({ queryKey: key, queryFn: () => api.caseJobs({ path: { caseId } }) });
  const [draft, setDraft] = useState({ starts: '', ends: '', location: '' });
  const [report, setReport] = useState<Record<string, string>>({});
  const schedule = useMutation({
    mutationFn: () =>
      api.scheduleJob({
        path: { caseId },
        body: { startsAt: new Date(draft.starts).toISOString(), endsAt: new Date(draft.ends).toISOString(), location: draft.location || null },
      }),
    onSuccess: () => {
      setDraft({ starts: '', ends: '', location: '' });
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
  const complete = useMutation({
    mutationFn: (visitId: string) => api.completeJob({ body: { visitId, note: report[visitId] ?? '' } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
  if (!jobs.data) return null;
  return (
    <Panel data-region="case-jobs">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('dispatch.caseTitle')}</SectionLabel>
        <ul className="flex flex-col gap-2 text-dense">
          {jobs.data.jobs.map((job) => (
            <li key={job.visitId} data-case-job={job.visitId} data-job-status={job.status} className="flex flex-col gap-1">
              <span className="text-ink">{formatDateTime(job.startsAt, format)} · {job.technicianName ?? t('dispatch.unassigned')}{job.location ? ` · ${job.location}` : ''}</span>
              {job.outcomeNote ? <span className="text-ink-600">{job.outcomeNote}</span> : null}
              {job.status === 'planned' && job.technicianId ? (
                <div className="flex flex-wrap gap-2">
                  <input className={`${INPUT} flex-1`} data-job-report placeholder={t('dispatch.report')} value={report[job.visitId] ?? ''}
                    onChange={(event) => setReport({ ...report, [job.visitId]: event.target.value })} />
                  <Button size="small" data-job-complete onClick={() => complete.mutate(job.visitId)}>{t('dispatch.complete')}</Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        <form className="flex flex-wrap items-end gap-2" data-job-form onSubmit={(event) => { event.preventDefault(); schedule.mutate(); }}>
          <input className={INPUT} type="datetime-local" required data-job-starts aria-label={t('dispatch.starts')} value={draft.starts} onChange={(event) => setDraft({ ...draft, starts: event.target.value })} />
          <input className={INPUT} type="datetime-local" required data-job-ends aria-label={t('dispatch.ends')} value={draft.ends} onChange={(event) => setDraft({ ...draft, ends: event.target.value })} />
          <input className={`${INPUT} flex-1`} data-job-location placeholder={t('dispatch.location')} value={draft.location} onChange={(event) => setDraft({ ...draft, location: event.target.value })} />
          <Button size="small" type="submit" data-job-schedule disabled={schedule.isPending}>{t('dispatch.schedule')}</Button>
        </form>
        {[schedule.error, complete.error].map((error, index) => (error ? <p key={index} className="text-dense text-danger-text">{errorText(error as ApiError)}</p> : null))}
      </div>
    </Panel>
  );
}
